import { CookieConsent } from '@liveauctioneers/hammer-ui-core/cookieConsent';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { LinkProvider } from '@liveauctioneers/hammer-ui-core/providers';
import { ToastContainer } from '@liveauctioneers/hammer-ui-core/toast';
import loadable from '@loadable/component';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BrowserOutdatedBanner } from '@/components/BrowserOutdatedBanner/BrowserOutdatedBanner';
import { DismissableGetMobileBanner } from '@/components/DismissableGetMobileBanner/DismissableGetMobileBanner';
import { DismissableGetWebNotificationsBanner } from '@/components/DismissableGetWebNotificationsBanner/DismissableGetWebNotificationsBanner';
import { ExitIntentSavePrompt } from '@/components/ExitIntentSavePrompt/ExitIntentSavePrompt.loadable';
import { FeedbackWidgetContainer } from '@/components/FeedbackWidgetContainer/FeedbackWidgetContainer.loadable';
import ForgotPasswordAction from '@/components/Modals/AuthModal/pages/ForgotPassword/ForgotPasswordAction.loadable';
import SignupAction from '@/components/Modals/SignUp/SignupAction.loadable';
import NewSafariSentinel from '@/components/NewSafariSentinel/NewSafariSentinel';
import { PageTracker } from '@/components/PageTracker/PageTracker';
import Signifyd from '@/components/Signifyd/Signifyd';
import { SuspendedBidderBanner } from '@/components/SuspendedBidderBanner/SuspendedBidderBanner';
import { TermsOfServiceBanner } from '@/components/TermsOfServiceBanner/TermsOfServiceBanner';
import ZendeskWidget from '@/components/ZendeskWidget/ZendeskWidget';
import { useLocationChangeListener } from '@/pages/App/hooks/useLocationChangeListener';
import { useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getIsBot } from '@/redux/modules/analytics';
import { getIsBrowserOutdated } from '@/redux/modules/browser';
import { useTrackCookieConsentAnalytics } from '@/utils/amplitude/analytics/cookieConsent/trackCookieConsent.analytics';

import AppJSX from './AppJSX';
import Auth from './Auth';
import { GoogleAuthWidget } from './GoogleAuthWidget.loadable';
import Hydrate from './Hydrate';
import UnpaidInvoiceCheck from './UnpaidInvoiceCheck';

const OpenReplay = loadable(
    /* #__LOADABLE__ */
    () => import('@/components/OpenReplay/OpenReplay'),
    {
        ssr: false,
    }
);

const AmplitudeProvider = loadable(
    /* #__LOADABLE__ */
    () => import('@/components/Analytics/AmplitudeProvider'),
    {
        ssr: false,
    }
);

const BidderNotificationsSubscriber = loadable(
    /* #__LOADABLE__ */
    () => import('@/components/LiveEventSubscribers/BidderNotificationsSubscriber'),
    {
        ssr: false,
    }
);

const GlobalNotificationsSubscriber = loadable(
    /* #__LOADABLE__ */
    () => import('@/components/LiveEventSubscribers/GlobalNotificationsSubscriber'),
    {
        ssr: false,
    }
);

type Props = {
    children: React.ReactNode;
};

const App = ({ children }: Props) => {
    useLocationChangeListener();
    const cookieConsentTracking = useTrackCookieConsentAnalytics();

    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { formatMessage } = useIntl();
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const isBiddingConsole = pathname.includes('/console/');
    const isAuthRedirect = pathname.includes('/auth/');
    const isCapital = pathname.includes('/capital');
    const isVideo = pathname.includes('/video/');
    const isCheckout = pathname.includes('/checkout/');
    const isCeckoutATG = pathname.includes('/checkoutATG/');
    const isBrowserOutdated = useAppSelector(getIsBrowserOutdated);
    const isBot = useAppSelector(getIsBot);

    const showHeaderAndFooter =
        !isBiddingConsole && !isCapital && !isVideo && !isCheckout && !isCeckoutATG && !isAuthRedirect;

    return (
        <React.StrictMode>
            <LinkProvider onLinkClick={navigate}>
                <OpenReplay />
                <AmplitudeProvider />
                <ToastContainer />
                <ExitIntentSavePrompt />
                <CookieConsent tracking={cookieConsentTracking} />
                <Signifyd />
                <Hydrate />
                <PageTracker />
                <FeedbackWidgetContainer />
                <UnpaidInvoiceCheck />
                <ZendeskWidget />
                {userLoggedIn && (
                    <>
                        <BidderNotificationsSubscriber />
                        <GlobalNotificationsSubscriber formatMessage={formatMessage} />
                        <DismissableGetWebNotificationsBanner />
                    </>
                )}
                <TermsOfServiceBanner />
                {isBrowserOutdated && !isBot && <BrowserOutdatedBanner />}
                {!isBiddingConsole && <DismissableGetMobileBanner />}
                <SuspendedBidderBanner />
                <NewSafariSentinel />
                <Auth />
                <GoogleAuthWidget />
                <SignupAction />
                <ForgotPasswordAction />
                <AppJSX showHeaderAndFooter={showHeaderAndFooter}>{children}</AppJSX>
            </LinkProvider>
        </React.StrictMode>
    );
};

export default React.memo(App);
